import { useEffect, useState } from "react";
import clsx from "clsx";
import dayjs from "dayjs";
import { Account, ICreateRentalRequestPayload, PcComponent, RentalType } from "@app/types";
import { MAX_LEN } from "@app/constants";
import { PortalDialogProps } from "@app/services/modal.service";
import RentalRequestService from "@app/services/http/rental.request.service";
import { useT } from "@app/contexts/preferences.context";
import { categoryOf, locationsOf, partArtBackground, partIconShadow } from "@app/pages/pc-component/pc-parts";
import Modal from "../modal/modal.component";
import { addToast } from "../toast/toast.service";
import { IcCheck, IcCircleCheck, IcMapPin, IcMinus, IcPlus, IcShield, IcX } from "../icons";
import { btn, btnGhost, btnLg, btnPrimary, iconBtn, input, select } from "../ui";

interface RentalRequestModalProps {
  component: PcComponent;
  /** Tài khoản đang đăng nhập, dùng cho nút "Dùng thông tin tài khoản" */
  account?: Account;
  onTerms?: () => void;
}

/** Số lượng tối đa giống ô chọn 1–5 của modal cũ */
const MAX_QTY = 5;

/**
 * Gửi yêu cầu thuê linh kiện. Body gửi lên giữ nguyên như modal cũ (ICreateRentalRequestPayload).
 * Đóng với { sent: true } khi gửi thành công, { see: true } khi bấm "Xem yêu cầu của tôi".
 */
function RentalRequestModal({ portalDialogRef, portalData }: PortalDialogProps<RentalRequestModalProps>) {
  const t = useT();
  const component = portalData?.component as PcComponent;
  const account = portalData?.account;
  const locations = component ? locationsOf(component) : [];

  const [rentalType, setRentalType] = useState<RentalType>("daily");
  const [location, setLocation] = useState(locations[0] || "");
  const [startDate, setStartDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [duration, setDurationState] = useState(7);
  const [quantity, setQuantityState] = useState(1);

  const [fullName, setFullName] = useState(account?.fullName || "");
  const [phone, setPhone] = useState(account?.phone || "");
  const [email, setEmail] = useState(account?.email || "");
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");
  const [agree, setAgree] = useState(false);

  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (!component) return null;

  const cat = categoryOf(component.category);
  const monthly = rentalType === "monthly";
  const unitName = monthly ? t("tháng") : t("ngày");
  const unitPrice = monthly ? component.monthly_rental_rate : component.daily_rental_rate;
  const rent = (unitPrice || 0) * duration * quantity;
  const deposit = (component.deposit || 0) * quantity;
  const end = dayjs(startDate).add(duration, monthly ? "month" : "day");

  // tối đa 365 ngày hoặc 24 tháng, giống kiểm tra ở API
  const setDuration = (n: number) => setDurationState(Math.min(monthly ? 24 : 365, Math.max(1, Math.floor(n) || 1)));

  // đổi sang thuê theo tháng mà số đang chọn vượt 24 thì hạ xuống
  useEffect(() => {
    if (monthly && duration > 24) setDurationState(24);
  }, [monthly, duration]);
  const setQuantity = (n: number) => setQuantityState(Math.min(MAX_QTY, Math.max(1, Math.floor(n) || 1)));

  const phoneDigits = phone.replace(/[\s.-]/g, "");
  const errors: Record<string, string> = {};

  if (!startDate) errors.startDate = "Chọn ngày nhận hàng";

  if (!location) errors.location = "Chọn địa điểm thuê";

  if (!fullName.trim()) errors.fullName = "Vui lòng nhập họ tên";

  if (!phoneDigits) errors.phone = "Vui lòng nhập số điện thoại";
  else if (!/^\+?\d{9,12}$/.test(phoneDigits)) errors.phone = "Số điện thoại chưa đúng, ví dụ 0901234567";

  if (!address.trim()) errors.address = "Vui lòng nhập địa chỉ nhận hàng";

  if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errors.email = "Email chưa đúng định dạng";

  const err = (key: string) => (showErrors ? errors[key] : "");

  const handleSubmit = () => {
    if (Object.keys(errors).length || !agree) {
      setShowErrors(true);

      return;
    }

    const payload: ICreateRentalRequestPayload = {
      componentId: component._id,
      rentalType,
      location,
      startDate,
      duration,
      quantity,
      fullName,
      phone,
      email,
      address,
      note,
    };

    setBusy(true);

    // Lỗi từ server (hết hàng, sai địa điểm…) đã được HttpService báo bằng toast, ở đây chỉ mở lại nút gửi
    RentalRequestService.createRentalRequest(payload).subscribe({
      next: (res) => {
        setBusy(false);

        if (res.statusCode === 200) {
          setSent(true);
        } else {
          addToast({
            text: res.message || "Gửi Yêu Cầu Thất Bại. Liên Hệ CSKH Để Được Hỗ Trợ",
            status: "inValid",
          });
        }
      },
      error: () => setBusy(false),
    });
  };

  const close = () => portalDialogRef.close(sent ? { sent: true } : undefined);

  const field = (label: string, required: boolean, control: JSX.Element, error?: string, help?: JSX.Element | string) => (
    <label className="flex min-w-0 flex-col gap-1 text-[12.5px] font-semibold text-muted">
      <span>
        {t(label)} {required ? <i className="not-italic text-bad">*</i> : <small className="font-normal text-faint">({t("tuỳ chọn")})</small>}
      </span>
      {control}
      {error ? <small className="font-medium text-bad">{t(error)}</small> : help && <small className="font-normal text-faint">{help}</small>}
    </label>
  );

  const stepper = (value: number, onChange: (n: number) => void, max?: number) => (
    <div className="inline-flex w-fit items-center overflow-hidden rounded-[10px] border border-line bg-surface">
      <button type="button" className="grid h-10 w-9 place-items-center text-muted hover:bg-surface-2" onClick={() => onChange(value - 1)}>
        <IcMinus className="h-4 w-4" />
      </button>
      <input
        type="number"
        min={1}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-10 w-12 border-x border-line bg-transparent text-center font-semibold tabular-nums text-fg outline-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <button type="button" className="grid h-10 w-9 place-items-center text-muted hover:bg-surface-2" onClick={() => onChange(value + 1)}>
        <IcPlus className="h-4 w-4" />
      </button>
    </div>
  );

  const thumb = (
    // ảnh thu nhỏ theo bản thiết kế (.lk-mini .lk-art): gradient + ô icon màu danh mục
    <span
      className="relative grid h-14 w-[72px] flex-none place-items-center overflow-hidden rounded-[9px]"
      style={{ background: partArtBackground(cat.color), color: cat.color }}
    >
      <span
        className="grid h-9 w-9 place-items-center rounded-[10px] bg-surface"
        style={{ boxShadow: partIconShadow(cat.color) }}
      >
        <cat.icon className="h-5 w-5" />
      </span>
    </span>
  );

  return (
    <Modal onCancel={close} buttonCancelInChildren={false}>
      <div className="flex max-h-[min(92vh,860px)] w-[min(900px,calc(100vw-32px))] flex-col text-fg">
        <div className="flex items-start justify-between gap-3 border-b border-line px-6 pb-4 pt-5">
          <div className="min-w-0">
            <h2 className="text-[19px] font-bold">{t("Gửi yêu cầu thuê")}</h2>
            <p className="m-0 mt-0.5 truncate text-[13px] text-muted">{component.name}</p>
          </div>
          <button type="button" className={clsx(iconBtn, "!h-8 !w-8")} aria-label={t("Đóng")} onClick={close}>
            <IcX className="h-4 w-4" />
          </button>
        </div>

        {sent ? (
          <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
            <span className="mb-2 grid h-14 w-14 place-items-center rounded-2xl bg-ok-soft text-ok">
              <IcCircleCheck className="h-7 w-7" />
            </span>
            <h3 className="text-lg font-bold">{t("Đã gửi yêu cầu thuê")}</h3>
            <p className="m-0 max-w-[46ch] text-muted">
              {t("PCRENDER sẽ gọi lại số")} <b className="tabular-nums text-fg">{phone}</b>{" "}
              {t("để xác nhận và hẹn giờ giao hàng.")}
            </p>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              <button type="button" className={clsx(btn, btnGhost)} onClick={close}>
                {t("Đóng")}
              </button>
              <button type="button" className={clsx(btn, btnPrimary)} onClick={() => portalDialogRef.close({ sent: true, see: true })}>
                {t("Xem yêu cầu của tôi")}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_300px] gap-5 overflow-y-auto px-6 py-5 max-[860px]:grid-cols-1">
            <div className="flex min-w-0 flex-col gap-3">
              <div className="flex items-center gap-3 rounded-xl border border-line p-2">
                {thumb}
                <div className="min-w-0">
                  <b className="block truncate">{component.name}</b>
                  <small className="line-clamp-1 text-xs text-muted">{component.description}</small>
                </div>
              </div>
              <p className="m-0 flex items-start gap-2 rounded-[10px] bg-brand-soft px-3 py-2 text-[12.5px] text-brand">
                <IcMapPin className="mt-px h-4 w-4 flex-none" />
                <span>
                  {t("Hiện chỉ giao và nhận lại linh kiện tại")} <b>{component.location}</b>.
                </span>
              </p>

              <h3 className="mt-1 text-sm font-bold">{t("Thông tin thuê")}</h3>
              <div className="flex flex-col gap-1">
                <span className="text-[12.5px] font-semibold text-muted">{t("Hình thức thuê")}</span>
                <div className="grid grid-cols-2 gap-2">
                  {(
                    [
                      ["daily", "Theo ngày", component.daily_rental_rate, "ngày"],
                      ["monthly", "Theo tháng", component.monthly_rental_rate, "tháng"],
                    ] as [RentalType, string, number, string][]
                  ).map(([key, label, price, unit]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setRentalType(key);
                        setDurationState(key === "monthly" ? 1 : 7);
                      }}
                      className={clsx(
                        "flex flex-col items-center rounded-xl border px-2 py-2 leading-tight",
                        rentalType === key ? "border-brand bg-brand-soft text-brand" : "border-line text-fg"
                      )}
                    >
                      <b className="text-sm">{t(label)}</b>
                      <small className={clsx("text-xs tabular-nums", rentalType === key ? "text-brand" : "text-muted")}>
                        {(price || 0).toLocaleString("vi-VN")}đ/{t(unit)}
                      </small>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-[1.2fr_1fr_1fr] items-start gap-3 max-[760px]:grid-cols-2 max-[760px]:[&>:first-child]:col-span-2">
                {field(
                  "Ngày nhận hàng",
                  true,
                  <input
                    type="date"
                    className={clsx(input, "w-full", err("startDate") && "!border-bad")}
                    min={dayjs().format("YYYY-MM-DD")}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />,
                  err("startDate")
                )}
                <div className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted">
                  <span>{t("Thời gian thuê")}</span>
                  {stepper(duration, setDuration)}
                  <small className="font-normal text-faint">
                    {unitName} · {t("trả ngày")} <b className="tabular-nums">{end.format("DD/MM/YYYY")}</b>
                  </small>
                </div>
                <div className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted">
                  <span>{t("Số lượng")}</span>
                  {stepper(quantity, setQuantity, MAX_QTY)}
                  <small className="font-normal text-faint">
                    {t("Tối đa")} {MAX_QTY}
                  </small>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(monthly ? [1, 2, 3, 6] : [3, 7, 15, 30]).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setDuration(v)}
                    className={clsx(
                      "rounded-lg border px-2.5 py-1 text-[12.5px]",
                      duration === v ? "border-brand text-brand" : "border-line text-muted"
                    )}
                  >
                    {v} {unitName}
                  </button>
                ))}
              </div>

              <h3 className="mt-1 flex items-center justify-between gap-2 text-sm font-bold">
                {t("Người nhận")}
                {account && (
                  <button
                    type="button"
                    className="text-[12.5px] font-semibold text-brand"
                    onClick={() => {
                      setFullName(account.fullName || "");
                      setPhone(account.phone || "");
                      setEmail(account.email || "");
                    }}
                  >
                    {t("Dùng thông tin tài khoản")}
                  </button>
                )}
              </h3>
              <div className="grid grid-cols-2 gap-3 max-[760px]:grid-cols-1">
                {field(
                  "Họ tên",
                  true,
                  <input
                    className={clsx(input, "w-full", err("fullName") && "!border-bad")}
                    autoComplete="name"
                    maxLength={MAX_LEN.FULL_NAME}
                    value={fullName}
                    placeholder={t("Nhập họ và tên")}
                    onChange={(e) => setFullName(e.target.value)}
                  />,
                  err("fullName")
                )}
                {field(
                  "Số điện thoại",
                  true,
                  <input
                    type="tel"
                    className={clsx(input, "w-full tabular-nums", err("phone") && "!border-bad")}
                    autoComplete="tel"
                    maxLength={MAX_LEN.PHONE}
                    value={phone}
                    placeholder="0901234567"
                    onChange={(e) => setPhone(e.target.value)}
                  />,
                  err("phone")
                )}
              </div>
              <div className="grid grid-cols-[minmax(0,180px)_minmax(0,1fr)] gap-3 max-[760px]:grid-cols-1">
                {field(
                  "Địa điểm thuê",
                  true,
                  <select
                    className={clsx(select, "w-full", err("location") && "!border-bad")}
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  >
                    {locations.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>,
                  err("location")
                )}
                {field(
                  "Địa chỉ nhận hàng",
                  true,
                  <input
                    className={clsx(input, "w-full", err("address") && "!border-bad")}
                    autoComplete="street-address"
                    maxLength={MAX_LEN.ADDRESS}
                    value={address}
                    placeholder={t("Số nhà, tên đường, phường/xã…")}
                    onChange={(e) => setAddress(e.target.value)}
                  />,
                  err("address")
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 max-[760px]:grid-cols-1">
                {field(
                  "Email",
                  false,
                  <input
                    type="email"
                    className={clsx(input, "w-full", err("email") && "!border-bad")}
                    autoComplete="email"
                    maxLength={MAX_LEN.EMAIL}
                    value={email}
                    placeholder={t("Nhận xác nhận qua email")}
                    onChange={(e) => setEmail(e.target.value)}
                  />,
                  err("email")
                )}
                {field(
                  "Ghi chú",
                  false,
                  <input
                    className={clsx(input, "w-full")}
                    maxLength={MAX_LEN.NOTE}
                    value={note}
                    placeholder={t("Giờ nhận hàng, yêu cầu khác…")}
                    onChange={(e) => setNote(e.target.value)}
                  />
                )}
              </div>
            </div>

            <aside className="flex flex-col gap-3 self-start rounded-xl border border-line p-4">
              <h3 className="text-sm font-bold">{t("Tạm tính")}</h3>
              <div className="flex flex-col gap-1.5 rounded-[10px] bg-surface-2 px-3 py-2.5 text-[13px] tabular-nums">
                {(
                  [
                    ["Đơn giá", `${(unitPrice || 0).toLocaleString("vi-VN")}đ/${unitName}`],
                    ["Thời gian", `${duration} ${unitName}${quantity > 1 ? ` × ${quantity}` : ""}`],
                    ["Nhận → trả", `${dayjs(startDate).format("DD/MM/YYYY")} → ${end.format("DD/MM/YYYY")}`],
                  ] as [string, string][]
                ).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3">
                    <span className="text-muted">{t(k)}</span>
                    <b className="text-right">{v}</b>
                  </div>
                ))}
                <div className="mt-1 flex items-baseline justify-between gap-3 border-t border-line pt-2">
                  <b>{t("Tiền thuê")}</b>
                  <strong className="text-lg text-brand">{rent.toLocaleString("vi-VN")}đ</strong>
                </div>
              </div>
              <div className="flex items-center gap-2.5 rounded-[10px] bg-warn-soft px-3 py-2 text-warn">
                <IcShield className="h-5 w-5 flex-none" />
                <div className="flex flex-col">
                  <span className="text-xs">{t("Tiền cọc (hoàn lại)")}</span>
                  <b className="tabular-nums">{deposit.toLocaleString("vi-VN")}đ</b>
                </div>
              </div>
              <ul className="m-0 flex list-disc flex-col gap-1 pl-4 text-xs leading-snug text-muted">
                <li>{t("Chưa trừ tiền khi gửi yêu cầu. Tiền thuê và cọc thanh toán khi nhận hàng.")}</li>
                <li>{t("Tiền cọc sẽ được hoàn lại sau khi trả sản phẩm và không phát sinh hư hỏng.")}</li>
                <li>{t("Giá thuê đã bao gồm VAT (nếu có).")}</li>
              </ul>
              <label
                className={clsx(
                  "flex cursor-pointer items-start gap-2 text-[12.5px] leading-snug",
                  showErrors && !agree ? "text-bad" : "text-muted"
                )}
              >
                <input
                  type="checkbox"
                  className="mt-px h-4 w-4 flex-none accent-[var(--brand)]"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                />
                <span>
                  {t("Tôi đồng ý")}{" "}
                  <button type="button" className="font-semibold text-brand" onClick={() => portalData?.onTerms?.()}>
                    {t("điều khoản thuê linh kiện")}
                  </button>
                </span>
              </label>
              <button type="button" className={clsx(btn, btnPrimary, btnLg, "w-full")} disabled={busy} onClick={handleSubmit}>
                <IcCheck className="h-[18px] w-[18px]" />
                {t("Gửi yêu cầu thuê")}
              </button>
              <p className="m-0 text-center text-[11.5px] text-faint">
                {t("Yêu cầu của bạn sẽ được PCRENDER xác nhận trong thời gian sớm nhất.")}
              </p>
            </aside>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default RentalRequestModal;
